import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import useAuthStore from "./store/authStore";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AdminDashboard from "./pages/admin/Dashboard";
import MetaLayout from "./pages/meta/MetaLayout";
import MetaDashboard from "./pages/meta/Dashboard";
import MetaReports from "./pages/meta/Reports";
import CustomersCampaigns from "./pages/CustomersCampaigns";
import MetaLeads from "./pages/meta/Leads";
import MetaLeadDetail from "./pages/meta/LeadDetail";
import MetaFiles from "./pages/meta/Files";
import MetaFileDetail from "./pages/meta/FileDetail";
import MetaEligibilityCheck from "./pages/meta/EligibilityCheck";
import MetaFileReports from "./pages/meta/FileReports";
import MetaCallLogs from "./pages/meta/CallLogs";
import MetaPartners from "./pages/meta/Partners";
import MetaUserManagement from "./pages/meta/UserManagement";
import MetaChatbotReport from "./pages/meta/ChatbotReport";
import MetaInbox from "./pages/meta/Inbox";
import MetaAdPerformance from "./pages/meta/AdPerformance";
import AdminLeads from "./pages/admin/Leads";
import AdminUsers from "./pages/admin/Users";
import AdminApprovals from "./pages/admin/Approvals";
import AdminReports from "./pages/admin/Reports";
import AdminSettings from "./pages/admin/Settings";
import AdminAttendance from "./pages/admin/Attendance";
import DailyTrackingSheet from "./pages/admin/DailyTrackingSheet";
import LeaveManagement from "./pages/admin/LeaveManagement";
import LegacyUserMapping from "./pages/admin/LegacyUserMapping";
import TelecallerDashboard from "./pages/telecaller/Dashboard";
import TelecallerLeads from "./pages/telecaller/Leads";
import TelecallerFollowUps from "./pages/telecaller/FollowUps";
import TelecallerProfile from "./pages/telecaller/Profile";
import TelecallerAttendance from "./pages/telecaller/Attendance";
import TelecallerTrackReport from "./pages/telecaller/TrackReport";
import { TeamMembers, TeamData, TeamFiles, TeamCalls, TeamLeads } from "./pages/telecaller/team";
import LeadDetail from "./pages/LeadDetail";
import { FilesDashboard, FileDetailsPage } from "./pages/files";
import BankEligibilityAnalysis from "./pages/files/BankEligibilityAnalysis";
import EligibilityCheck from "./pages/files/EligibilityCheck";
import DataMigration from "./pages/files/DataMigration";
import PolicyMaster from "./pages/files/PolicyMaster";
import BankPolicyMaster from "./pages/admin/BankPolicyMaster";
import { RejectedCasesReport, GrowthPartnerReport, QualityReport, SalesOpsReport } from "./pages/files/reports";
import AdminLayout from "./layouts/AdminLayout";
import HrLayout from "./layouts/HrLayout";
import TelecallerLayout from "./layouts/TelecallerLayout";
import ManagerLayout from "./layouts/ManagerLayout";
import ManagerDashboard from "./pages/manager/ManagerDashboard";
import ManagerTeam from "./pages/manager/ManagerTeam";
import LoadingScreen from "./components/LoadingScreen";
import "./App.css";

// Role-neutral File redirect component
const RoleNeutralFileRedirect = () => {
  const { user } = useAuthStore();
  const baseRoute = user?.role === 'admin' ? '/admin' : (user?.role === 'hr' ? '/hr' : '/agent');
  // Get the file ID from URL
  const fileId = window.location.pathname.split('/files/')[1];
  return <Navigate to={`${baseRoute}/files/${fileId}`} replace />;
};

// Growth Partner roles (includes legacy role names)
const GP_ROLES = ['growth_partner', 'telecaller', 'sales_agent', 'team_leader', 'partner'];

// Roles with CRM operational access
const CRM_ACCESS_ROLES = ['admin', 'ops', 'manager', ...GP_ROLES];

// HR role - limited to attendance/leave
const HR_ROLE = 'hr';

const ProtectedRoute = ({ children, requiredRole }) => {
  const { isAuthenticated, user, isLoading } = useAuthStore();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const userRole = user?.role;

  // HR gets its own limited route set (Dashboard, Attendance, Leave Management, Files - view only)
  if (userRole === HR_ROLE && requiredRole !== HR_ROLE) {
    return <Navigate to="/hr" replace />;
  }
  if (requiredRole === HR_ROLE && userRole !== HR_ROLE && userRole !== 'admin') {
    return <Navigate to={userRole === 'manager' ? '/manager' : '/agent'} replace />;
  }

  // Manager role gets its own route set
  if (requiredRole === 'manager' && userRole !== 'manager' && userRole !== 'admin') {
    return <Navigate to={userRole === 'admin' ? '/admin' : '/agent'} replace />;
  }

  // If requiredRole is 'telecaller' or 'gp', allow all GP roles, ops, and manager
  if (requiredRole === 'telecaller' && !CRM_ACCESS_ROLES.includes(userRole)) {
    return <Navigate to={userRole === 'admin' ? '/admin' : (userRole === 'manager' ? '/manager' : '/agent')} replace />;
  }
  
  // For admin role, strict check
  if (requiredRole === 'admin' && userRole !== 'admin') {
    // Ops can access admin routes for CRM operations
    if (userRole === 'ops') {
      return children; // Allow ops to access admin CRM routes
    }
    // Managers now have their own dedicated routes
    if (userRole === 'manager') {
      return <Navigate to="/manager" replace />;
    }
    return <Navigate to="/agent" replace />;
  }

  return children;
};

// Meta section guard: authenticated + meta_access is the master gate.
const MetaProtectedRoute = ({ children }) => {
  const { isAuthenticated, user, isLoading } = useAuthStore();
  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!user?.meta_access) {
    const base = user?.role === 'admin' ? '/admin' : user?.role === 'manager' ? '/manager' : user?.role === 'hr' ? '/hr' : '/agent';
    return <Navigate to={base} replace />;
  }
  return children;
};

function App() {
  const { loadAuth, isLoading, isAuthenticated, user } = useAuthStore();

  useEffect(() => {
    loadAuth();
  }, []);

  if (isLoading) {
    return <LoadingScreen />;
  }

  // Determine the correct base route based on user role
  const getBaseRoute = () => {
    if (!user) return '/login';
    if (user.role === 'admin') return '/admin';
    if (user.role === 'manager') return '/manager';
    if (user.role === HR_ROLE) return '/hr';
    return '/agent';
  };

  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={
          isAuthenticated ? (
            <Navigate to={getBaseRoute()} replace />
          ) : (
            <Login />
          )
        } />
        <Route path="/register" element={
          isAuthenticated ? (
            <Navigate to={getBaseRoute()} replace />
          ) : (
            <Register />
          )
        } />

        {/* Admin Routes */}
        <Route path="/admin" element={
          <ProtectedRoute requiredRole="admin">
            <AdminLayout />
          </ProtectedRoute>
        }>
          <Route index element={<AdminDashboard />} />
          <Route path="leads" element={<AdminLeads />} />
          <Route path="leads/:id" element={<LeadDetail />} />
          <Route path="files" element={<FilesDashboard />} />
          <Route path="files/:fileId" element={<FileDetailsPage />} />
          <Route path="files/:fileId/eligibility" element={<BankEligibilityAnalysis />} />
          <Route path="files/:fileId/check-eligibility" element={<EligibilityCheck />} />
          <Route path="files/migrate" element={<DataMigration />} />
          <Route path="files/policies" element={<PolicyMaster />} />
          <Route path="files/bank-policies" element={<BankPolicyMaster />} />
          <Route path="files/reports/rejected" element={<RejectedCasesReport />} />
          <Route path="files/reports/growth-partner" element={<GrowthPartnerReport />} />
          <Route path="files/reports/quality" element={<QualityReport />} />
          <Route path="files/reports/sales-ops" element={<SalesOpsReport />} />
          <Route path="approvals" element={<AdminApprovals />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="users/legacy-mapping" element={<LegacyUserMapping />} />
          <Route path="reports" element={<AdminReports />} />
          <Route path="team/leads" element={<TeamLeads />} />
          <Route path="tracking" element={<DailyTrackingSheet />} />
          <Route path="attendance" element={<AdminAttendance />} />
          <Route path="leave" element={<LeaveManagement />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="customers-campaigns" element={<CustomersCampaigns />} />
        </Route>

        {/* Telecaller/Agent Routes */}
        <Route path="/agent" element={
          <ProtectedRoute requiredRole="telecaller">
            <TelecallerLayout />
          </ProtectedRoute>
        }>
          <Route index element={<TelecallerLeads />} />
          <Route path="dashboard" element={<TelecallerDashboard />} />
          <Route path="leads" element={<TelecallerLeads />} />
          <Route path="leads/:id" element={<LeadDetail />} />
          <Route path="files" element={<FilesDashboard />} />
          <Route path="files/:fileId" element={<FileDetailsPage />} />
          <Route path="files/:fileId/eligibility" element={<BankEligibilityAnalysis />} />
          <Route path="files/:fileId/check-eligibility" element={<EligibilityCheck />} />
          <Route path="files/policies" element={<PolicyMaster />} />
          <Route path="followups" element={<TelecallerFollowUps />} />
          <Route path="tracking" element={<TelecallerTrackReport />} />
          <Route path="attendance" element={<TelecallerAttendance />} />
          <Route path="leave" element={<LeaveManagement />} />
          <Route path="profile" element={<TelecallerProfile />} />
          <Route path="team" element={<TeamMembers />} />
          <Route path="team/data" element={<TeamData />} />
          <Route path="team/files" element={<TeamFiles />} />
          <Route path="team/calls" element={<TeamCalls />} />
          <Route path="team/leads" element={<TeamLeads />} />
          <Route path="reports" element={<AdminReports />} />
          <Route path="customers-campaigns" element={<CustomersCampaigns />} />
        </Route>

        {/* Manager Routes - Admin-like access without User Management */}
        <Route path="/manager" element={
          <ProtectedRoute requiredRole="manager">
            <ManagerLayout />
          </ProtectedRoute>
        }>
          <Route index element={<ManagerDashboard />} />
          <Route path="files" element={<FilesDashboard />} />
          <Route path="files/:fileId" element={<FileDetailsPage />} />
          <Route path="files/:fileId/eligibility" element={<BankEligibilityAnalysis />} />
          <Route path="files/:fileId/check-eligibility" element={<EligibilityCheck />} />
          <Route path="files/policies" element={<PolicyMaster />} />
          <Route path="files/bank-policies" element={<BankPolicyMaster />} />
          <Route path="files/reports/rejected" element={<RejectedCasesReport />} />
          <Route path="files/reports/growth-partner" element={<GrowthPartnerReport />} />
          <Route path="files/reports/quality" element={<QualityReport />} />
          <Route path="files/reports/sales-ops" element={<SalesOpsReport />} />
          <Route path="reports" element={<AdminReports />} />
          <Route path="tracking" element={<DailyTrackingSheet />} />
          <Route path="attendance" element={<AdminAttendance />} />
          <Route path="leave" element={<LeaveManagement />} />
          <Route path="team" element={<ManagerTeam />} />
          <Route path="team/calls" element={<TeamCalls />} />
          <Route path="team/leads" element={<TeamLeads />} />
          <Route path="customers-campaigns" element={<CustomersCampaigns />} />
        </Route>

        {/* HR Routes - Dashboard & Files are view only, Attendance & Leave same as Admin */}
        <Route path="/hr" element={
          <ProtectedRoute requiredRole="hr">
            <HrLayout />
          </ProtectedRoute>
        }>
          <Route index element={<AdminDashboard />} />
          <Route path="attendance" element={<AdminAttendance />} />
          <Route path="leave" element={<LeaveManagement />} />
          <Route path="files" element={<FilesDashboard />} />
          <Route path="files/policies" element={<PolicyMaster />} />
          <Route path="files/:fileId" element={<FileDetailsPage />} />
        </Route>

        {/* Role-neutral File route - redirects to correct role-based route */}
        <Route path="/files/:fileId" element={
          <ProtectedRoute>
            <RoleNeutralFileRedirect />
          </ProtectedRoute>
        } />

        {/* Meta CRM - dedicated full-screen section with the old navy sidebar */}
        <Route path="/meta" element={
          <MetaProtectedRoute>
            <MetaLayout />
          </MetaProtectedRoute>
        }>
          <Route index element={<MetaDashboard />} />
          <Route path="reports" element={<MetaReports />} />
          <Route path="leads" element={<MetaLeads />} />
          <Route path="leads/:leadId" element={<MetaLeadDetail />} />
          <Route path="files" element={<MetaFiles />} />
          <Route path="files/:leadId" element={<MetaFileDetail />} />
          <Route path="files/:leadId/check-eligibility" element={<MetaEligibilityCheck />} />
          <Route path="file-reports" element={<MetaFileReports />} />
          <Route path="call-logs" element={<MetaCallLogs />} />
          <Route path="chatbot-report" element={<MetaChatbotReport />} />
          <Route path="inbox" element={<MetaInbox />} />
          <Route path="ad-performance" element={<MetaAdPerformance />} />
          <Route path="partners" element={<MetaPartners />} />
          <Route path="users" element={<MetaUserManagement />} />
        </Route>

        {/* Default redirect */}
        <Route path="*" element={
          <Navigate to={isAuthenticated ? getBaseRoute() : '/login'} replace />
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
